// src/pages/Campus/CampusPage.jsx
// Campus Hiring Tracker — track candidates from offer to joining
// Route: /campus

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button, Input, Select, DatePicker, Tag, Tooltip,
  Modal, Popconfirm, message as antMsg, Spin, Upload,
  Progress, Divider, Switch,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  UploadOutlined, DownloadOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined, UserOutlined,
  FileExcelOutlined, EyeOutlined, SyncOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as XLSX from 'xlsx';
dayjs.extend(relativeTime);

// Safe date parse — returns null for Excel serial numbers or invalid strings
function safeDay(val) {
  if (!val || val === '' || val === '-') return null;
  if (!isNaN(Number(val)) && Number(val) > 1000) return null; // Excel serial — ignore
  const d = dayjs(val);
  return d.isValid() ? d : null;
}

const { TextArea } = Input;

// ── Design tokens ─────────────────────────────────────────────────
const C = {
  navy:'#1B2D6B', blue:'#1E6FBF', lblue:'#EAF1F8',
  green:'#16a34a', lgreen:'#F0FDF4',
  red:'#DC2626',   lred:'#FEF2F2',
  amber:'#B45309', lamber:'#FFFBEB',
  purple:'#7c3aed',lpurple:'#f5f3ff',
  teal:'#0891b2',  lteal:'#ecfeff',
  orange:'#ea580c',lorange:'#fff7ed',
  text1:'#1a2d4a', text2:'#4a6580', text3:'#7a96b0',
  border:'#DCE6F0', surf:'#F5F8FC', white:'#FFFFFF',
};

const STATUS_CONFIG = {
  'Active':            { color:C.green,  bg:C.lgreen,   label:'Active',           icon:'🟢' },
  'Provisional Offer': { color:C.amber,  bg:C.lamber,   label:'Provisional Offer',icon:'📄' },
  'Final Offer':       { color:C.blue,   bg:C.lblue,    label:'Final Offer',      icon:'📋' },
  'Denied Joining':    { color:C.red,    bg:C.lred,     label:'Denied Joining',   icon:'❌' },
  'Resigned':          { color:C.purple, bg:C.lpurple,  label:'Resigned',         icon:'🚪' },
  'To Be Mapped':      { color:'#b45309', bg:'#fffbeb',  label:'To Be Mapped',     icon:'⚠️' },
  'InActive':          { color:C.purple, bg:C.lpurple,  label:'InActive',         icon:'🚪' },
  'Denied':            { color:C.red,    bg:C.lred,     label:'Denied',           icon:'❌' },
  // ── Variant names (old uploaded data) — map to same display ──
  'Offered':           { color:C.amber,  bg:C.lamber,   label:'Offered',          icon:'📄' },
  'Offer Sent':        { color:C.amber,  bg:C.lamber,   label:'Offer Sent',       icon:'📄' },
  'Denied':            { color:C.red,    bg:C.lred,     label:'Denied',           icon:'❌' },
  'Not Joined':        { color:C.red,    bg:C.lred,     label:'Not Joined',       icon:'❌' },
};

// Only canonical statuses in filters/dropdowns (exclude legacy variant aliases)
const STATUSES = ['Active','InActive','Denied','Offered','To Be Mapped'];
const POSITIONS = ['BE','ABM','TBM','TM','KAM','ASM','Other'];

// ── "To be Mapped" helper ──────────────────────────────────────────
const TBM = ({ value }) =>
  value && value.toString().trim()
    ? <span>{value}</span>
    : <span style={{ fontSize:11, fontWeight:600, color:C.amber,
        background:C.lamber, padding:'1px 7px', borderRadius:10,
        border:`1px solid ${C.amber}44` }}>
        To be Mapped
      </span>;

const StatusTag = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color:C.text3, bg:C.surf, label:status, icon:'❓' };
  return (
    <Tag style={{ borderRadius:20, fontWeight:700, fontSize:11, padding:'2px 10px',
      background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.color}33` }}>
      {cfg.icon} {cfg.label}
    </Tag>
  );
};

const emptyForm = () => ({
  name:'', mobile:'', email:'', position:'BE',
  campusMonth:'', campusDate:'', campusLocation:'', joiningMonth:'',
  collegeName:'', qualification:'',
  empCode:'', dateOfJoining:'', division:'', hq:'',
  finalStatus:'Provisional Offer', hasJoined:false,
  resignationDate:'', resignationReason:'', remarks:'',
});

const Label = ({ children, required }) => (
  <label style={{ display:'block', fontSize:11, fontWeight:700,
    color:C.text2, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.4px' }}>
    {children}{required && <span style={{ color:C.red }}> *</span>}
  </label>
);

// ══════════════════════════════════════════════════════════════════
export default function CampusPage() {
  const [records,  setRecords]  = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [total,    setTotal]    = useState(0);

  // Filters
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPos,    setFilterPos]    = useState('all');
  const [filterMonth,  setFilterMonth]  = useState('all');
  const [filterJoined, setFilterJoined] = useState('all');
  const [filterYear,   setFilterYear]   = useState('all'); // dashboard year filter
  const [page,         setPage]         = useState(1);
  const PAGE = 50;

  // Modals
  const [addModal,    setAddModal]    = useState(false);
  const [editRecord,  setEditRecord]  = useState(null);
  const [bulkModal,   setBulkModal]   = useState(false);
  const [detailRec,   setDetailRec]   = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [form,        setForm]        = useState(emptyForm());
  const [activeTab,   setActiveTab]   = useState('dashboard'); // dashboard | grid

  // Bulk upload state
  const [bulkRows,    setBulkRows]    = useState([]);
  const [bulkStep,    setBulkStep]    = useState(0);
  const [bulkResult,  setBulkResult]  = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // SharePoint sync (campus hires)
  const [syncLoading, setSyncLoading] = useState(false);
  const handleSharepointSync = async () => {
    setSyncLoading(true);
    try {
      const res = await api.post('/sharepoint-sync/sync-campus');
      const r = res.data;
      antMsg.success(
        `Campus sync done — ${r.inserted} records inserted${r.failed ? `, ${r.failed} failed` : ''}`,
        5,
      );
      load();
    } catch (e) {
      antMsg.error('Campus sync failed: ' + (e?.response?.data?.message || e?.message || 'Unknown error'));
    } finally {
      setSyncLoading(false);
    }
  };

  // ── Load ──────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE) });
      if (search)                       params.append('search',      search);
      if (filterStatus !== 'all')       params.append('status',      filterStatus);
      if (filterPos    !== 'all')       params.append('position',    filterPos);
      if (filterMonth  !== 'all')       params.append('campusMonth', filterMonth);
      if (filterJoined !== 'all')       params.append('hasJoined',   filterJoined);

      const statsParams = filterYear !== 'all' ? `?year=${filterYear}` : '';
      const [rRes, sRes] = await Promise.all([
        api.get(`/campus?${params}`),
        api.get(`/campus/stats${statsParams}`),
      ]);
      setRecords(rRes.data?.data ?? rRes.data ?? []);
      setTotal(rRes.data?.total ?? 0);
      setStats(sRes.data?.data ?? sRes.data);
    } catch { antMsg.error('Failed to load campus data'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterPos, filterMonth, filterJoined, filterYear, page]);

  useEffect(() => { load(); }, [load]);

  // ── Campus months from data ───────────────────────────────────
  const campusMonths = useMemo(() => {
    const s = new Set(records.map(r => r.campusMonth).filter(Boolean));
    return [...s].sort();
  }, [records]);

  // ── Positions from stats.byPosition (ALL data, not just current page) ──
  const dataPositions = useMemo(() => {
    if (stats?.byPosition?.length) {
      return stats.byPosition.map(p => p.position).filter(Boolean).sort();
    }
    // Fallback to current page records if stats not loaded yet
    const s = new Set(records.map(r => r.position).filter(Boolean));
    return [...s].sort();
  }, [stats, records]);

  // ── Save (add / edit) ─────────────────────────────────────────
  async function handleSave() {
    if (!form.name.trim()) { antMsg.error('Name is required'); return; }
    if (form.hasJoined && !form.mobile && !form.email) {
      antMsg.error('Mobile or Email is required for joined candidates'); return;
    }
    setSaving(true);
    try {
      if (editRecord) {
        await api.patch(`/campus/${editRecord.id}`, form);
        antMsg.success('✅ Record updated');
        setEditRecord(null);
      } else {
        await api.post('/campus', form);
        antMsg.success('✅ Candidate added');
        setAddModal(false);
      }
      setForm(emptyForm());
      load();
    } catch (e) {
      antMsg.error(e?.response?.data?.message || e.message || 'Save failed');
    } finally { setSaving(false); }
  }

  // ── Delete ────────────────────────────────────────────────────
  async function handleDelete(id) {
    try {
      await api.delete(`/campus/${id}`);
      antMsg.success('Deleted');
      load();
    } catch { antMsg.error('Delete failed'); }
  }

  // ── Parse date value from Excel cell (serial, string, Date object) ──
  function parseExcelDate(val) {
    if (!val || val === '' || val === '-') return '';
    // Excel serial number
    const num = Number(val);
    if (!isNaN(num) && num > 1000 && num < 100000) {
      const d = new Date(Date.UTC(1899, 11, 30) + num * 86400000);
      return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
    }
    if (val instanceof Date) return val.toISOString().slice(0, 10);
    const s = String(val).trim();
    // Try DD/MM/YYYY and DD-MM-YYYY
    const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`;
    const d = dayjs(s);
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
  }

  // ── Normalise incoming status names to canonical values ────────
  function normalizeStatus(raw) {
    if (!raw) return 'Provisional Offer';
    const s = raw.trim().toLowerCase();
    if (['offered','offer sent','offer','provisional offer'].includes(s)) return 'Provisional Offer';
    if (['final offer'].includes(s))                                       return 'Final Offer';
    if (['active'].includes(s))                                            return 'Active';
    if (['inactive'].includes(s))                                          return 'Inactive';
    if (['denied','not joined','denied joining','deny'].includes(s))       return 'Denied Joining';
    if (['resigned','resignation'].includes(s))                            return 'Resigned';
    if (['to be mapped','to-be-mapped','tobemapped'].includes(s))          return 'To Be Mapped';
    return raw.trim();
  }

  // ── Derive "Apr '26" from a YYYY-MM-DD string ─────────────────
  function toMonthLabel(dateStr) {
    if (!dateStr) return '';
    const d = dayjs(dateStr);
    return d.isValid() ? d.format("MMM 'YY") : '';
  }

  // ── Bulk Excel parse ──────────────────────────────────────────
  function handleExcelUpload(file) {
    const reader = new FileReader();
    reader.onload = e => {
      const wb = XLSX.read(e.target.result, { type:'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval:'' });

      const mapped = raw.map((r, i) => {
        // Accept both new column names (screenshot) and legacy names
        const campusDateRaw  = r['Campus Date']     || r['campusDate']    || '';
        const dojRaw         = r['DOJ']             || r['DateOfJoining'] || r['dateOfJoining'] || '';
        const campusDate     = parseExcelDate(campusDateRaw);
        const dateOfJoining  = parseExcelDate(dojRaw);
        const resignRaw      = r['Resignation Date']|| r['resignationDate'] || '';
        const resignationDate = parseExcelDate(resignRaw);

        // resignationDate set → always Resigned, regardless of Status column
        const statusRaw = String(r['Status'] || r['Final Status'] || r['finalStatus'] || '').trim();
        const finalStatus = resignationDate
          ? 'Resigned'
          : normalizeStatus(statusRaw || (dateOfJoining ? 'Active' : 'Provisional Offer'));

        return {
          _row:    i + 2,
          _errors: [],
          // New column names first, legacy fallbacks second
          name:             String(r['Name']            || r['name']           || '').trim(),
          mobile:           String(r['Contact No']      || r['Mobile No']      || r['mobile']       || '').trim(),
          email:            String(r['Email ID']        || r['EmailId']        || r['email']        || '').trim(),
          position:         String(r['Designation']     || r['Position']       || r['position']     || '').trim(),
          campusDate,
          campusLocation:   String(r['Campus Location'] || r['campusLocation'] || '').trim(),
          // Derive months automatically — backend also does this, but set here for preview accuracy
          campusMonth:      String(r['Campus Month']    || r['campusMonth']    || '').trim() || toMonthLabel(campusDate),
          dateOfJoining,
          joiningMonth:     String(r['Joining Month']   || r['joiningMonth']   || '').trim() || toMonthLabel(dateOfJoining),
          collegeName:      String(r['College Name']    || r['collegeName']    || '').trim(),
          qualification:    String(r['Qualification']   || r['qualification']  || '').trim(),
          empCode:          String(r['Emp Code']        || r['empCode']        || '').trim(),
          division:         String(r['Division']        || r['division']       || '').trim(),
          hq:               String(r['HQ']              || r['hq']             || '').trim(),
          finalStatus,
          resignationDate,
          resignationReason:String(r['Resignation reason']||r['resignationReason']||'').trim(),
          remarks:          String(r['Remarks']         || r['remarks']        || '').trim(),
          hasJoined:        !!dateOfJoining,
        };
      }).filter(r => r.name);

      mapped.forEach(r => {
        if (!r.name) r._errors.push('Name required');
      });
      setBulkRows(mapped);
      setBulkStep(1);
    };
    reader.readAsArrayBuffer(file);
    return false;
  }

  async function handleBulkSubmit() {
    const valid = bulkRows.filter(r => !r._errors?.length);
    if (!valid.length) { antMsg.warning('No valid rows'); return; }
    setBulkLoading(true);
    try {
      const clean = valid.map(({ _row, _errors, ...rest }) => rest);
      const res   = await api.post('/campus/bulk', { rows: clean });
      setBulkResult(res.data?.data ?? res.data);
      setBulkStep(2);
      load();
    } catch (e) {
      antMsg.error(e?.response?.data?.message || 'Bulk upload failed');
    } finally { setBulkLoading(false); }
  }

  function downloadTemplate() {
    // Column order matches the screenshot exactly.
    // Campus Month and Joining Month are auto-derived from Campus Date and DOJ on import.
    const sampleRows = [
      {
        'Status':           'Active',
        'Campus Date':      '2026-03-01',
        'Campus Location':  'Ahmedabad Campus',
        'Designation':      'Medical Representative',
        'Emp Code':         'L04480',
        'Name':             'John Smith',
        'Email ID':         'john@erislifesciences.com',
        'DOJ':              '2026-03-01',
        'Division':         'Nikkos',
        'HQ':               'Mumbai',
        'Contact No':       '9876543210',
        'College Name':     'Gujarat University',
        'Qualification':    'B.Pharm',
        'Resignation Date': '',
        'Remarks':          '',
      },
      {
        'Status':           'Resigned',
        'Campus Date':      '2026-03-01',
        'Campus Location':  'Delhi Campus',
        'Designation':      'Area Business Executive',
        'Emp Code':         'L04481',
        'Name':             'Priya Sharma',
        'Email ID':         'priya@erislifesciences.com',
        'DOJ':              '2026-03-01',
        'Division':         'Aspire',
        'HQ':               'Delhi',
        'Contact No':       '9876543211',
        'College Name':     'Delhi University',
        'Qualification':    'B.Sc',
        'Resignation Date': '2026-04-15',
        'Remarks':          'Joined competitor',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleRows);
    ws['!cols'] = Object.keys(sampleRows[0]).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Campus Hires');
    XLSX.writeFile(wb, 'CampusHires_Template.xlsx');
  }

  function exportExcel() {
    const rows = records.map(r => {
      const doj = r.dateOfJoining ? dayjs(r.dateOfJoining) : null;
      const resDate = r.resignationDate ? dayjs(r.resignationDate) : null;
      const tenureDays = doj
        ? (resDate || dayjs()).diff(doj, 'day')
        : null;
      const tenureRange = tenureDays === null ? '' :
        tenureDays <= 10  ? '0–10 Days' :
        tenureDays <= 30  ? '10 Days–1 Month' :
        tenureDays <= 60  ? '1–2 Months' :
        tenureDays <= 90  ? '2–3 Months' :
        tenureDays <= 120 ? '3–4 Months' :
        tenureDays <= 150 ? '4–5 Months' :
        tenureDays <= 180 ? '5–6 Months' : '6+ Months';

      return {
        'Status':           r.finalStatus || '',
        'Campus Date':      r.campusDate  ? dayjs(r.campusDate).format('DD MMM YYYY') : '',
        'Campus Location':  r.campusLocation || '',
        'Designation':      r.position || '',
        'Emp Code':         r.empCode  || '',
        'Name':             r.name,
        'Email ID':         r.email    || '',
        'DOJ':              doj ? doj.format('DD MMM YYYY') : '',
        'Division':         r.division || '',
        'HQ':               r.hq       || '',
        'Contact No':       r.mobile   || '',
        'College Name':     r.collegeName   || '',
        'Qualification':    r.qualification || '',
        'Resignation Date': resDate ? resDate.format('DD MMM YYYY') : '',
        'Tenure (Days)':    tenureDays !== null ? tenureDays : '',
        'Tenure Range':     tenureRange,
        'Remarks':          r.remarks || '',
      };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Campus Hires');
    XLSX.writeFile(wb, `CampusHires_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    antMsg.success('Exported!');
  }

  // ── Form modal (add + edit share same modal) ──────────────────
  const FormModal = ({ open, onClose, title }) => (
    <Modal open={open} onCancel={onClose} title={<span style={{ fontWeight:800 }}>{title}</span>}
      footer={null} width={680}>
      <div style={{ display:'flex', flexDirection:'column', gap:12, padding:'8px 0' }}>

        {/* ── Section: Candidate info ── */}
        <div style={{ background:C.surf, borderRadius:10, padding:'12px 16px',
          border:`1px solid ${C.border}` }}>
          <div style={{ fontSize:11, fontWeight:800, color:C.navy,
            textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:10 }}>
            👤 Candidate Information
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <Label required>Full Name</Label>
              <Input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}
                placeholder="Aman Sharma" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>Designation / Position</Label>
              <Input value={form.position}
                onChange={e=>setForm(p=>({...p,position:e.target.value}))}
                placeholder="e.g. Medical Representative, BE, ABM…"
                style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>Mobile No</Label>
              <Input value={form.mobile} onChange={e=>setForm(p=>({...p,mobile:e.target.value}))}
                placeholder="9876543210" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>Email ID</Label>
              <Input value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}
                placeholder="aman@example.com" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>College Name</Label>
              <Input value={form.collegeName} onChange={e=>setForm(p=>({...p,collegeName:e.target.value}))}
                placeholder="Jamia Hamdard University" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>Qualification</Label>
              <Input value={form.qualification} onChange={e=>setForm(p=>({...p,qualification:e.target.value}))}
                placeholder="MBA Pharma Management" style={{ borderRadius:8 }}/>
            </div>
          </div>
        </div>

        {/* ── Section: Campus Event ── */}
        <div style={{ background:C.surf, borderRadius:10, padding:'12px 16px',
          border:`1px solid ${C.border}` }}>
          <div style={{ fontSize:11, fontWeight:800, color:C.blue,
            textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:10 }}>
            🏫 Campus Event Details
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
            <div>
              <Label>Campus Month</Label>
              <Input value={form.campusMonth} onChange={e=>setForm(p=>({...p,campusMonth:e.target.value}))}
                placeholder="Apr '26" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>Campus Date</Label>
              <DatePicker value={safeDay(form.campusDate)}
                onChange={d=>setForm(p=>({...p,campusDate:d?d.format('YYYY-MM-DD'):''}))}
                style={{ width:'100%', borderRadius:8 }} format="DD MMM YYYY"/>
            </div>
            <div>
              <Label>Campus Location</Label>
              <Input value={form.campusLocation} onChange={e=>setForm(p=>({...p,campusLocation:e.target.value}))}
                placeholder="Delhi" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>Joining Month</Label>
              <Input value={form.joiningMonth} onChange={e=>setForm(p=>({...p,joiningMonth:e.target.value}))}
                placeholder="Apr '26" style={{ borderRadius:8 }}/>
            </div>
          </div>
        </div>

        {/* ── Section: Joining details ── */}
        <div style={{ background:C.surf, borderRadius:10, padding:'12px 16px',
          border:`1px solid ${C.border}` }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            marginBottom:10 }}>
            <span style={{ fontSize:11, fontWeight:800, color:C.green,
              textTransform:'uppercase', letterSpacing:'0.6px' }}>
              ✅ Joining Details (fill after joining)
            </span>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:12, color:C.text3 }}>Has Joined?</span>
              <Switch checked={form.hasJoined}
                onChange={v=>setForm(p=>({...p,hasJoined:v,
                  finalStatus:v?'Active':(p.finalStatus==='Active'?'Offer Sent':p.finalStatus)}))}
                style={{ background:form.hasJoined?C.green:'#cbd5e1' }}/>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
            <div>
              <Label>Employee Code</Label>
              <Input value={form.empCode} onChange={e=>setForm(p=>({...p,empCode:e.target.value}))}
                placeholder="E13750" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>Date of Joining</Label>
              <DatePicker value={safeDay(form.dateOfJoining)}
                onChange={d=>setForm(p=>({...p,dateOfJoining:d?d.format('YYYY-MM-DD'):'',
                  hasJoined:!!d}))}
                style={{ width:'100%', borderRadius:8 }} format="DD MMM YYYY"/>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.finalStatus} onChange={v=>setForm(p=>({...p,finalStatus:v}))}
                style={{ width:'100%' }}>
                {STATUSES.map(s=><Select.Option key={s} value={s}>{s}</Select.Option>)}
              </Select>
            </div>
            <div>
              <Label>Division</Label>
              <Input value={form.division} onChange={e=>setForm(p=>({...p,division:e.target.value}))}
                placeholder="Biocon Immuno" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <Label>HQ</Label>
              <Input value={form.hq} onChange={e=>setForm(p=>({...p,hq:e.target.value}))}
                placeholder="Delhi" style={{ borderRadius:8 }}/>
            </div>
          </div>
        </div>

        {/* ── Section: Resignation (conditional) ── */}
        {(['Resigned','Denied Joining','Inactive'].includes(form.finalStatus)) && (
          <div style={{ background:C.lred, borderRadius:10, padding:'12px 16px',
            border:`1px solid ${C.red}33` }}>
            <div style={{ fontSize:11, fontWeight:800, color:C.red,
              textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:10 }}>
              🚪 Resignation Details
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div>
                <Label>Resignation Date</Label>
                <DatePicker value={safeDay(form.resignationDate)}
                  onChange={d=>setForm(p=>({...p,resignationDate:d?d.format('YYYY-MM-DD'):''}))}
                  style={{ width:'100%', borderRadius:8 }} format="DD MMM YYYY"/>
              </div>
              <div>
                <Label>Resignation Reason</Label>
                <Input value={form.resignationReason}
                  onChange={e=>setForm(p=>({...p,resignationReason:e.target.value}))}
                  placeholder="Better opportunity" style={{ borderRadius:8 }}/>
              </div>
            </div>
          </div>
        )}

        {/* Remarks */}
        <div>
          <Label>Remarks</Label>
          <TextArea rows={2} value={form.remarks}
            onChange={e=>setForm(p=>({...p,remarks:e.target.value}))}
            placeholder="Any additional notes…" style={{ borderRadius:8 }}/>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}
            style={{ background:C.navy, borderRadius:8, fontWeight:700 }}>
            {editRecord ? 'Save Changes' : 'Add Candidate'}
          </Button>
        </div>
      </div>
    </Modal>
  );

  // ── RENDER ────────────────────────────────────────────────────
  return (
    <div style={{ background:'#f0f4f8', minHeight:'100vh' }}>

      {/* ── Header ── */}
      <div style={{ background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:'20px 28px', boxShadow:'0 4px 20px rgba(27,45,107,0.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:'#fff',
              display:'flex', alignItems:'center', gap:10 }}>
              🎓 Campus Hiring Tracker
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginTop:3 }}>
              Track campus candidates from offer letter to active joining
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff' }}>
              Refresh
            </Button>
            <Button icon={<DownloadOutlined/>} onClick={exportExcel}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff' }}>
              Export
            </Button>
            <Button icon={<FileExcelOutlined/>} onClick={() => { setBulkStep(0); setBulkRows([]); setBulkResult(null); setBulkModal(true); }}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff' }}>
              Bulk Upload
            </Button>
            <Button icon={<SyncOutlined spin={syncLoading}/>} onClick={handleSharepointSync} loading={syncLoading}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.18)',
                border:'1px solid rgba(255,255,255,0.4)', color:'#fff', fontWeight:600 }}>
              Sync SharePoint
            </Button>
            <Button type="primary" icon={<PlusOutlined/>}
              onClick={() => { setForm(emptyForm()); setAddModal(true); }}
              style={{ borderRadius:8, background:'#fff', color:C.navy,
                border:'none', fontWeight:700 }}>
              Add Candidate
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding:'24px 28px 60px' }}>

        {/* ── Tab switcher ── */}
        <div style={{ display:'flex', gap:0, marginBottom:20, background:C.white,
          borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden',
          alignSelf:'flex-start', width:'fit-content',
          boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
          {[
            { key:'dashboard', label:'📊 Dashboard' },
            { key:'grid',      label:'📋 Data Grid'  },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{ padding:'10px 24px', cursor:'pointer', border:'none',
                borderBottom:activeTab===t.key ? `3px solid ${C.navy}` : '3px solid transparent',
                background:activeTab===t.key ? C.white : C.surf,
                fontWeight:activeTab===t.key ? 800 : 600, fontSize:13,
                color:activeTab===t.key ? C.navy : C.text3, transition:'all .15s' }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && !records.length
          ? <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
          : activeTab === 'dashboard'
            ? <DashboardView
                stats={stats}
                records={records}
                filterYear={filterYear}
                setFilterYear={setFilterYear}
              />
            : <GridView
                records={records} total={total} page={page} setPage={setPage}
                search={search} setSearch={setSearch}
                filterStatus={filterStatus} setFilterStatus={setFilterStatus}
                filterPos={filterPos} setFilterPos={setFilterPos}
                filterMonth={filterMonth} setFilterMonth={setFilterMonth}
                filterJoined={filterJoined} setFilterJoined={setFilterJoined}
                campusMonths={campusMonths}
                dataPositions={dataPositions}
                onEdit={rec => { setEditRecord(rec); setForm({ ...emptyForm(), ...rec }); }}
                onDelete={handleDelete}
                onView={setDetailRec}
              />
        }
      </div>

      {/* ── Add Modal ── */}
      <FormModal open={addModal} onClose={() => setAddModal(false)}
        title="🎓 Add Campus Candidate"/>

      {/* ── Edit Modal ── */}
      <FormModal open={!!editRecord} onClose={() => setEditRecord(null)}
        title={`✏️ Edit — ${editRecord?.name || ''}`}/>

      {/* ── Detail Modal ── */}
      <Modal open={!!detailRec} onCancel={() => setDetailRec(null)}
        title={<span style={{ fontWeight:800 }}>👤 {detailRec?.name}</span>}
        footer={<Button onClick={() => setDetailRec(null)}>Close</Button>} width={520}>
        {detailRec && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <StatusTag status={detailRec.finalStatus}/>
            {[
              ['Position',       detailRec.position],
              ['Mobile',         detailRec.mobile || '—'],
              ['Email',          detailRec.email  || '—'],
              ['College',        detailRec.collegeName || '—'],
              ['Qualification',  detailRec.qualification || '—'],
              ['Campus Month',   detailRec.campusMonth || '—'],
              ['Campus Location',detailRec.campusLocation || '—'],
              ['Emp Code',       detailRec.empCode || '—'],
              ['Date of Joining',safeDay(detailRec.dateOfJoining)?.format('DD MMM YYYY') ?? '—'],
              ['Division',       detailRec.division || '—'],
              ['HQ',             detailRec.hq || '—'],
              ['Remarks',        detailRec.remarks || '—'],
            ].map(([l,v]) => (
              <div key={l} style={{ display:'flex', gap:10 }}>
                <span style={{ width:130, flexShrink:0, fontSize:12, fontWeight:700, color:C.text3 }}>{l}</span>
                {typeof v === 'string' && v ? <span style={{ fontSize:13, color:C.text1 }}>{v}</span> : (v ? <span style={{ fontSize:13, color:C.text1 }}>{v}</span> : <TBM value={null}/>)}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* ── Bulk Upload Modal ── */}
      <Modal open={bulkModal} onCancel={() => setBulkModal(false)}
        title={<span style={{ fontWeight:800 }}>📤 Bulk Upload Campus Hires</span>}
        footer={null} width={700}>
        <div style={{ padding:'8px 0' }}>
          {bulkStep === 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <Button icon={<DownloadOutlined/>} onClick={downloadTemplate}
                style={{ borderRadius:8, width:'fit-content' }}>
                Download Excel Template
              </Button>
              <Upload.Dragger accept=".xlsx,.xls"
                beforeUpload={handleExcelUpload} showUploadList={false}>
                <p style={{ fontSize:32, margin:'8px 0' }}>📂</p>
                <p style={{ fontWeight:600 }}>Click or drag Excel file here</p>
                <p style={{ fontSize:12, color:C.text3 }}>
                  .xlsx / .xls — Use the template above for correct column headers
                </p>
              </Upload.Dragger>
            </div>
          )}

          {bulkStep === 1 && bulkRows.length > 0 && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between',
                alignItems:'center', marginBottom:12 }}>
                <span style={{ fontWeight:700, color:C.text1 }}>
                  {bulkRows.filter(r=>!r._errors?.length).length} valid &nbsp;·&nbsp;
                  <span style={{ color:C.red }}>{bulkRows.filter(r=>r._errors?.length).length} errors</span>
                  &nbsp;of {bulkRows.length} rows
                </span>
                <Button size="small" onClick={() => { setBulkStep(0); setBulkRows([]); }}>
                  Re-upload
                </Button>
              </div>
              <div style={{ maxHeight:300, overflow:'auto', borderRadius:10,
                border:`1px solid ${C.border}` }}>
                {bulkRows.slice(0, 30).map((r, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10,
                    padding:'8px 14px',
                    background:r._errors?.length ? C.lred : (i%2===0 ? C.white : C.surf),
                    borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ width:28, fontSize:11, color:C.text3 }}>#{r._row}</span>
                    <span style={{ flex:1, fontWeight:600, fontSize:13 }}>{r.name || '—'}</span>
                    <span style={{ fontSize:11, color:C.text3 }}>{r.position}</span>
                    <span style={{ fontSize:11, color:C.text3 }}>{r.campusLocation}</span>
                    {r._errors?.length
                      ? <Tag color="red" style={{ fontSize:10 }}>{r._errors[0]}</Tag>
                      : <Tag color="green" style={{ fontSize:10 }}>✓ Valid</Tag>}
                  </div>
                ))}
                {bulkRows.length > 30 && (
                  <div style={{ padding:'8px 14px', fontSize:12, color:C.text3,
                    textAlign:'center' }}>
                    …and {bulkRows.length - 30} more rows
                  </div>
                )}
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
                <Button onClick={() => setBulkModal(false)}>Cancel</Button>
                <Button type="primary" loading={bulkLoading} onClick={handleBulkSubmit}
                  disabled={!bulkRows.filter(r=>!r._errors?.length).length}
                  style={{ background:C.navy, borderRadius:8, fontWeight:700 }}>
                  Upload {bulkRows.filter(r=>!r._errors?.length).length} Rows
                </Button>
              </div>
            </div>
          )}

          {bulkStep === 2 && bulkResult && (
            <div style={{ textAlign:'center', padding:20 }}>
              <div style={{ fontSize:48 }}>
                {bulkResult.failed === 0 ? '✅' : '⚠️'}
              </div>
              <div style={{ fontSize:20, fontWeight:800, color:C.navy, margin:'10px 0' }}>
                {bulkResult.success} imported successfully
              </div>
              {bulkResult.failed > 0 && (
                <div style={{ color:C.red, fontWeight:600 }}>
                  {bulkResult.failed} rows failed
                </div>
              )}
              <Button type="primary" onClick={() => setBulkModal(false)}
                style={{ marginTop:16, background:C.navy, borderRadius:8 }}>
                Done
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Dashboard View — KPI cards + charts
// ── Shared breakdown table used by Campus-wise and Division-wise tabs ──
function ConvBreakdownTable({ rows, nameLabel, excludeCols = [] }) {
  if (!rows?.length) return <div style={{ padding:24, textAlign:'center', color:C.text3 }}>No data</div>;
  const tot = rows.reduce((s,r) => ({
    total:s.total+r.total, active:s.active+r.active, inactive:s.inactive+r.inactive,
    toBeM:s.toBeM+r.toBeM, offered:s.offered+r.offered, denied:s.denied+r.denied,
  }), { total:0, active:0, inactive:0, toBeM:0, offered:0, denied:0 });
  const allCols = [
    { key:'total',       label:'Total Selected', color:'#93c5fd', bg:'transparent'   },
    { key:'active',      label:'Active',         color:'#86efac', bg:'#f0fdf4'        },
    { key:'activePct',   label:'Active %',       color:'#86efac', bg:'transparent', pct:true },
    { key:'inactive',    label:'InActive',       color:'#fca5a5', bg:C.lpurple        },
    { key:'inactivePct', label:'InActive %',     color:'#fca5a5', bg:'transparent', pct:true },
    { key:'toBeM',       label:'To Be Mapped',   color:'#fde68a', bg:'#fffbeb'        },
    { key:'offered',     label:'Offered',        color:'#fed7aa', bg:C.lamber         },
    { key:'denied',      label:'Denied',         color:'#fca5a5', bg:C.lred           },
  ];
  const cols = allCols
    .filter(c => !excludeCols.includes(c.key))
    .map((c, i, arr) => ({ ...c, last: i === arr.length - 1 }));
  // Augment rows with computed % columns
  const augmented = rows.map(r => ({
    ...r,
    activePct:   r.total > 0 ? Math.round((r.active   / r.total) * 100) : 0,
    inactivePct: r.total > 0 ? Math.round((r.inactive / r.total) * 100) : 0,
  }));
  const totAug = {
    ...tot,
    activePct:   tot.total > 0 ? Math.round((tot.active   / tot.total) * 100) : 0,
    inactivePct: tot.total > 0 ? Math.round((tot.inactive / tot.total) * 100) : 0,
  };
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
        <thead>
          <tr style={{ background:C.navy }}>
            <th style={{ padding:'9px 14px', fontSize:11, fontWeight:700,
              color:'rgba(255,255,255,0.75)', textAlign:'left', minWidth:200,
              position:'sticky', left:0, background:C.navy,
              borderRight:'1px solid rgba(255,255,255,0.25)',
              textTransform:'uppercase', letterSpacing:'0.5px' }}>{nameLabel}</th>
            {cols.map(c=>(
              <th key={c.key} style={{ padding:'9px 14px', fontSize:11, fontWeight:700,
                color:c.color, textAlign:'center', whiteSpace:'nowrap',
                borderRight:c.last?'none':'1px solid rgba(255,255,255,0.15)',
                textTransform:'uppercase', letterSpacing:'0.5px' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {augmented.map((r,i)=>(
            <tr key={r.name||i} style={{ borderBottom:`1px solid ${C.border}`,
              background:i%2===0?C.white:'#fafcff' }}>
              <td style={{ padding:'9px 14px', fontSize:13, fontWeight:600, color:C.text1,
                position:'sticky', left:0, background:i%2===0?C.white:'#fafcff',
                borderRight:`1px solid ${C.border}`, whiteSpace:'nowrap',
                maxWidth:240, overflow:'hidden', textOverflow:'ellipsis' }}
                title={r.name}>{r.name||'—'}</td>
              {cols.map(c=>(
                <td key={c.key} style={{ padding:'9px 14px', textAlign:'center', fontSize:c.pct?12:13,
                  fontWeight:700, color:r[c.key]>0?c.color:'#d1d5db',
                  background:r[c.key]>0&&!c.pct?c.bg:'transparent',
                  borderRight:c.last?'none':`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
                  {c.pct
                    ? (r[c.key]>0
                        ? <span style={{ fontWeight:800, color:c.color }}>{r[c.key]}%</span>
                        : <span style={{ color:'#d1d5db' }}>—</span>)
                    : (r[c.key]>0?r[c.key]:'0')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background:C.navy, borderTop:`2px solid ${C.navy}` }}>
            <td style={{ padding:'10px 14px', fontWeight:800, fontSize:13, color:'#fff',
              position:'sticky', left:0, background:C.navy,
              borderRight:'1px solid rgba(255,255,255,0.15)', textAlign:'left' }}>Total</td>
            {cols.map(c=>(
              <td key={c.key} style={{ padding:'10px 14px', textAlign:'center',
                fontWeight:800, fontSize: c.pct?12:14, color:c.color,
                borderRight:c.last?'none':'1px solid rgba(255,255,255,0.15)' }}>
                {c.pct ? `${totAug[c.key]}%` : totAug[c.key]}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
function DashboardView({ stats, records, filterYear, setFilterYear }) {
  const [tbmPos,     setTbmPos]     = React.useState(null);
  const [tbmList,       setTbmList]       = React.useState([]);
  const [tbmLoading,    setTbmLoading]    = React.useState(false);

  // Generic count drill-down modal
  const [drillModal, setDrillModal] = React.useState({ visible:false, title:'', rows:[], loading:false });
  const [tbmEditRec,    setTbmEditRec]    = React.useState(null);
  const [tbmForm,       setTbmForm]       = React.useState({});
  const [tbmSaving,     setTbmSaving]     = React.useState(false);
  const [expandedMonths, setExpandedMonths] = React.useState(new Set());
  const [convTab, setConvTab] = React.useState('month'); // 'month' | 'campus' | 'division'
  const toggleMonth = (month) => setExpandedMonths(prev => {
    const next = new Set(prev);
    next.has(month) ? next.delete(month) : next.add(month);
    return next;
  });

  async function loadTbm(position) {
    if (tbmPos === position) { setTbmPos(null); setTbmList([]); return; } // toggle off
    setTbmPos(position);
    setTbmLoading(true);
    try {
      const url = position ? `/campus/tbm?position=${encodeURIComponent(position)}` : '/campus/tbm';
      const res = await api.get(url);
      setTbmList(res.data?.data ?? res.data ?? []);
    } catch { setTbmList([]); }
    finally { setTbmLoading(false); }
  }

  async function openDrill(title, filters) {
    setDrillModal({ visible:true, title, rows:[], loading:true });
    try {
      const params = new URLSearchParams({ limit:'2000', ...filters });
      if (filterYear !== 'all') params.append('year', filterYear);
      const res = await api.get(`/campus?${params}`);
      const rows = res.data?.data ?? res.data ?? [];
      setDrillModal(d => ({ ...d, rows, loading:false }));
    } catch {
      setDrillModal(d => ({ ...d, loading:false }));
    }
  }

  function openTbmEdit(rec) {
    setTbmEditRec(rec);
    setTbmForm({
      empCode:      rec.empCode      || '',
      division:     rec.division     || '',
      hq:           rec.hq           || '',
      dateOfJoining:rec.dateOfJoining|| '',
      finalStatus:  rec.finalStatus  || 'Active',
      remarks:      rec.remarks      || '',
    });
  }

  async function saveTbmEdit() {
    if (!tbmEditRec) return;
    setTbmSaving(true);
    try {
      await api.patch(`/campus/${tbmEditRec.id}`, tbmForm);
      antMsg.success('✅ Record updated');
      setTbmEditRec(null);
      // Refresh TBM list
      if (tbmPos) loadTbm(tbmPos);
    } catch (e) {
      antMsg.error(e?.response?.data?.message || 'Save failed');
    } finally { setTbmSaving(false); }
  }

  if (!stats) return <div style={{ textAlign:'center', padding:60 }}><Spin/></div>;

  const kpis = [
    { icon:'🎓', label:'Total Selected Candidates', value:stats.total,                    color:C.navy,   bg:'#eff6ff' },
    { icon:'✅', label:'Joined',           value:stats.joined,                   color:C.blue,   bg:C.lblue   },
    { icon:'🟢', label:'Active',            value:stats.active,                   color:C.green,  bg:C.lgreen  },
    { icon:'📄', label:'Offered',           value:stats.provisional,              color:C.amber,  bg:C.lamber  },
    { icon:'⚠️', label:'To be Mapped',     value:stats.tbm,                      color:'#b45309',bg:'#fffbeb' },
    { icon:'🚪', label:'Resigned',         value:stats.resigned,                 color:C.purple, bg:C.lpurple },
    { icon:'❌', label:'Denied Joining',   value:stats.denied,                   color:C.red,    bg:C.lred    },
    { icon:'📈', label:'Conversion Rate',  value:`${stats.conversionRate}%`,     color:C.teal,   bg:C.lteal   },
  ];


  const availableYears = stats?.availableYears || [];

  // FY label: fiscal year key Y → "Oct 'YY – Sep 'YY+1"
  const fyLabel = (yr) => {
    const y1 = String(yr).slice(2);
    const y2 = String(yr + 1).slice(2);
    return `Oct '${y1} – Sep '${y2}`;
  };
  const fyLabelFull = (yr) => `Oct ${yr} – Sep ${yr + 1}`;

  return (
    <div>

      {/* ── Year Filter (Oct–Sep fiscal cycle) ── */}
      {availableYears.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:10,
          marginBottom:20, flexWrap:'wrap',
          background:C.white, borderRadius:12, padding:'12px 18px',
          border:`1px solid ${C.border}`,
          boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize:13, fontWeight:700, color:C.text2 }}>📅 Filter by Year:</span>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            <button onClick={() => setFilterYear('all')} style={{
              padding:'5px 16px', borderRadius:20, border:'none',
              cursor:'pointer', fontWeight:700, fontSize:12,
              background: filterYear === 'all' ? C.navy : C.surf,
              color:      filterYear === 'all' ? '#fff' : C.text2,
              boxShadow:  filterYear === 'all' ? '0 2px 8px rgba(27,45,107,0.25)' : 'none',
              transition:'all .15s',
            }}>All Years</button>
            {availableYears.map(yr => (
              <button key={yr} onClick={() => setFilterYear(String(yr))} style={{
                padding:'5px 16px', borderRadius:20, border:'none',
                cursor:'pointer', fontWeight:700, fontSize:12,
                background: filterYear === String(yr) ? C.blue : C.surf,
                color:      filterYear === String(yr) ? '#fff' : C.text2,
                boxShadow:  filterYear === String(yr) ? `0 2px 8px ${C.blue}44` : 'none',
                transition:'all .15s',
              }}>{fyLabel(yr)}</button>
            ))}
          </div>
          {filterYear !== 'all' && (
            <span style={{ fontSize:11, color:C.text3, background:'#eff6ff',
              padding:'3px 10px', borderRadius:20, border:`1px solid #bfdbfe` }}>
              Showing data for <strong style={{ color:C.blue }}>{fyLabelFull(Number(filterYear))}</strong>
            </span>
          )}
        </div>
      )}

      {/* KPI chips */}
      <div style={{ display:'grid',
        gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))', gap:12, marginBottom:24 }}>
        {kpis.map((k,i) => (
          <div key={i} style={{ background:k.bg, borderRadius:14,
            border:`1px solid ${k.color}22`, borderTop:`3px solid ${k.color}`,
            padding:'16px 18px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize:26, marginBottom:4 }}>{k.icon}</div>
            <div style={{ fontSize:28, fontWeight:900, color:k.color,
              lineHeight:1, letterSpacing:'-1px', marginBottom:4 }}>
              {k.value ?? '—'}
            </div>
            <div style={{ fontSize:10, fontWeight:700, color:'#7a96b0',
              textTransform:'uppercase', letterSpacing:'0.5px' }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* ── Designation Status Summary Table ── */}
      {(() => {
        // Uses stats.designationBreakdown from the API (ALL records, not just current page)
        const posRows = (stats.designationBreakdown || []);
        if (!posRows.length) return null;

        const totRow = {
          total:      posRows.reduce((s,r)=>s+r.total,0),
          provOffer:  posRows.reduce((s,r)=>s+r.provOffer,0),
          finalOffer: posRows.reduce((s,r)=>s+r.finalOffer,0),
          mapped:     posRows.reduce((s,r)=>s+r.mapped,0),
          toMap:      posRows.reduce((s,r)=>s+r.toMap,0),
          denied:     posRows.reduce((s,r)=>s+r.denied,0),
          resigned:   posRows.reduce((s,r)=>s+r.resigned,0),
          noCode:     posRows.reduce((s,r)=>s+r.noCode,0),
          noDiv:      posRows.reduce((s,r)=>s+r.noDiv,0),
          noHq:       posRows.reduce((s,r)=>s+r.noHq,0),
        };

        const TH = (extra = {}) => ({
          padding: '9px 14px', fontSize: 11, fontWeight: 700,
          color: '#fff', textAlign: 'center', whiteSpace: 'nowrap',
          borderRight: '1px solid rgba(255,255,255,0.15)', ...extra,
        });
        const TD = (color = C.text1, bg = 'transparent', align = 'center') => ({
          padding: '10px 14px', fontSize: 13, fontWeight: 700,
          color, background: bg, textAlign: align,
          borderRight: `1px solid ${C.border}`, whiteSpace: 'nowrap',
        });

        return (
          <div style={{ marginBottom: 20 }}>
            {/* Header */}
            <div style={{ marginBottom: 12, display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:15, fontWeight:700, color:C.text1 }}>
                📋 Designation-wise Status
              </span>
            </div>

            <div style={{ background:C.white, borderRadius:14,
              border:`1px solid ${C.border}`, overflow:'hidden',
              boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background: C.navy }}>
                      <th style={{ ...TH({ textAlign:'left', minWidth:130,
                        position:'sticky', left:0, background:C.navy,
                        borderRight:'1px solid rgba(255,255,255,0.25)' }) }}>
                        Designation
                      </th>
                      <th style={{ ...TH({ color:'#93c5fd', minWidth:100 }) }}>Total Selected</th>
                      <th style={{ ...TH({ color:'#86efac', minWidth:90  }) }}>Active</th>
                      <th style={{ ...TH({ color:'#fca5a5', minWidth:90  }) }}>InActive</th>
                      <th style={{ ...TH({ color:'#fde68a', minWidth:110 }) }}>To Be Mapped</th>
                      <th style={{ ...TH({ color:'#fed7aa', minWidth:90  }) }}>Offered</th>
                      <th style={{ ...TH({ color:'#fca5a5', minWidth:90, borderRight:'none' }) }}>Denied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posRows.map((row, i) => {
                      const rowBg = i%2===0 ? C.white : '#fafcff';
                      const cell = (val, color, bg='transparent', drillTitle=null, drillFilters=null) => (
                        <td style={{ padding:'10px 14px', textAlign:'center', fontSize:13,
                          fontWeight:700, color, background:bg,
                          borderRight:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
                          {val > 0
                            ? drillTitle
                              ? <span onClick={() => openDrill(drillTitle, drillFilters)}
                                  style={{ fontWeight:800, cursor:'pointer',
                                    textDecoration:'underline dotted', textUnderlineOffset:2 }}>{val}</span>
                              : <span style={{ fontWeight:800 }}>{val}</span>
                            : <span style={{ color:'#d1d5db' }}>0</span>}
                        </td>
                      );
                      return (
                        <tr key={row.pos} style={{ borderBottom:`1px solid ${C.border}`, background:rowBg }}>
                          <td style={{ padding:'10px 14px', fontSize:13, fontWeight:800,
                            color:C.navy, background:rowBg, position:'sticky', left:0,
                            borderRight:`1px solid ${C.border}`, textAlign:'left' }}>
                            {row.pos}
                          </td>
                          {cell(row.total,    C.blue,   '#eff6ff',
                            `${row.pos} — All Selected`,   { position:row.pos })}
                          {cell(row.mapped,   C.green,  row.mapped>0?'#f0fdf4':'transparent',
                            `${row.pos} — Active`,         { position:row.pos, status:'Active' })}
                          {cell(row.resigned, C.purple, row.resigned>0?C.lpurple:'transparent',
                            `${row.pos} — InActive`,       { position:row.pos, status:'InActive' })}
                          {/* To be Mapped — existing inline drill-down (kept) */}
                          <td style={{ padding:'10px 14px', textAlign:'center', fontSize:13,
                            fontWeight:700, background:tbmPos===row.pos?'#ffedd5':row.toMap>0?'#fffbeb':'transparent',
                            borderRight:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
                            {row.toMap > 0
                              ? <button onClick={() => loadTbm(row.pos)}
                                  style={{ background:'none', border:'none', cursor:'pointer',
                                    display:'flex', alignItems:'center', gap:5, padding:0, margin:'0 auto' }}>
                                  <span style={{ fontWeight:800, color:'#b45309', fontSize:14 }}>{row.toMap}</span>
                                  <span style={{ fontSize:10,
                                    color: tbmPos===row.pos ? '#fff' : '#b45309',
                                    background: tbmPos===row.pos ? '#b45309' : '#fde68a',
                                    padding:'1px 6px', borderRadius:10 }}>
                                    {tbmPos===row.pos ? '▲' : '▼'}
                                  </span>
                                </button>
                              : <span style={{ color:'#d1d5db' }}>0</span>}
                          </td>
                          {cell(row.provOffer, C.amber, row.provOffer>0?C.lamber:'transparent',
                            `${row.pos} — Offered`,        { position:row.pos, status:'Offered' })}
                          <td style={{ padding:'10px 14px', textAlign:'center', fontSize:13,
                            fontWeight:700, color:row.denied>0?C.red:C.text3,
                            background:row.denied>0?C.lred:'transparent',
                            borderRight:'none', whiteSpace:'nowrap' }}>
                            {row.denied > 0
                              ? <span onClick={() => openDrill(`${row.pos} — Denied`, { position:row.pos, status:'Denied' })}
                                  style={{ fontWeight:800, cursor:'pointer',
                                    textDecoration:'underline dotted', textUnderlineOffset:2 }}>{row.denied}</span>
                              : <span style={{ color:'#d1d5db' }}>0</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* TOTAL row */}
                  <tfoot>
                    <tr style={{ background:C.navy, borderTop:`2px solid ${C.navy}` }}>
                      {[
                        { val:'TOTAL',          color:'#fff',     style:{ textAlign:'left', position:'sticky', left:0, background:C.navy } },
                        { val:totRow.total,     color:'#93c5fd'  },
                        { val:totRow.mapped,    color:'#86efac'  },
                        { val:totRow.resigned,  color:'#fca5a5'  },
                        { val:totRow.toMap,     color:'#fde68a'  },
                        { val:totRow.provOffer, color:'#fed7aa'  },
                        { val:totRow.denied,    color:'#fca5a5', style:{ borderRight:'none' } },
                      ].map((c, ci) => (
                        <td key={ci} style={{ padding:'10px 14px', textAlign:'center',
                          fontWeight:800, fontSize:14, color:c.color,
                          borderRight:'1px solid rgba(255,255,255,0.15)', ...c.style }}>
                          {c.val}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Legend */}
              <div style={{ padding:'10px 16px', borderTop:`1px solid ${C.border}`,
                background:C.surf, display:'flex', gap:18, flexWrap:'wrap', alignItems:'center' }}>
                {[
                  { color:C.green,   bg:'#f0fdf4',  label:'Active — joined + all details filled' },
                  { color:C.purple,  bg:C.lpurple,  label:'InActive — resigned' },
                  { color:'#b45309', bg:'#fffbeb',  label:'To Be Mapped — status "To Be Mapped" + no DOJ' },
                  { color:C.amber,   bg:C.lamber,   label:'Offered — Provisional Offer + DOJ confirmed' },
                  { color:C.red,     bg:C.lred,     label:'Denied — denied joining' },
                ].map(({ color, bg, label }) => (
                  <div key={label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                    <div style={{ width:10, height:10, borderRadius:2,
                      background:bg, border:`1.5px solid ${color}`, flexShrink:0 }}/>
                    <span style={{ fontSize:11, color:C.text3 }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── TBM Drill-down panel ── */}
            {tbmPos && (
              <div style={{ marginTop:14, background:C.white, borderRadius:14,
                border:`1.5px solid ${C.orange}55`, overflow:'hidden',
                boxShadow:'0 2px 8px rgba(234,88,12,0.08)' }}>
                {/* Header */}
                <div style={{ padding:'12px 20px',
                  background:`linear-gradient(135deg,${C.orange},#f97316)`,
                  display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontWeight:700, fontSize:14, color:'#fff' }}>
                    ⚠️ To be Mapped — <strong>{tbmPos}</strong>
                    <span style={{ marginLeft:8, fontSize:12, fontWeight:400, opacity:0.85 }}>
                      Joined but missing Emp Code / Division / HQ
                    </span>
                  </span>
                  <button onClick={() => { setTbmPos(null); setTbmList([]); }}
                    style={{ background:'rgba(255,255,255,0.2)', border:'none',
                      borderRadius:6, color:'#fff', cursor:'pointer',
                      fontWeight:700, padding:'3px 10px', fontSize:12 }}>
                    ✕ Close
                  </button>
                </div>

                {tbmLoading
                  ? <div style={{ padding:24, textAlign:'center' }}><Spin/></div>
                  : tbmList.length === 0
                    ? <div style={{ padding:20, textAlign:'center', color:C.text3,
                        fontSize:13 }}>No records found</div>
                    : (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead>
                            <tr style={{ background:'#fff7ed',
                              borderBottom:`1px solid ${C.orange}33` }}>
                              {['#','Name','Emp Code','DOJ','Division','HQ','Campus Month','Remarks','Action'].map(h => (
                                <th key={h} style={{ padding:'8px 14px', textAlign:'left',
                                  fontSize:10, fontWeight:700, color:C.orange,
                                  textTransform:'uppercase', letterSpacing:'0.5px',
                                  whiteSpace:'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {tbmList.map((r, i) => (
                              <tr key={r.id}
                                style={{ borderBottom:`1px solid ${C.border}`,
                                  background: i%2===0 ? C.white : '#fffbf7' }}>
                                <td style={{ padding:'9px 14px', fontSize:11,
                                  color:C.text3, fontWeight:600 }}>{i+1}</td>
                                <td style={{ padding:'9px 14px' }}>
                                  <div style={{ fontWeight:700, fontSize:13,
                                    color:C.text1 }}>{r.name}</div>
                                  {r.mobile && <div style={{ fontSize:11,
                                    color:C.text3 }}>📱 {r.mobile}</div>}
                                </td>
                                <td style={{ padding:'9px 14px' }}>
                                  {r.empCode
                                    ? <span style={{ fontFamily:'monospace',
                                        fontWeight:700, color:C.navy }}>{r.empCode}</span>
                                    : <span style={{ fontSize:11, fontWeight:700,
                                        color:C.red, background:'#fef2f2',
                                        padding:'1px 7px', borderRadius:8,
                                        border:'1px solid #fca5a5' }}>Missing</span>}
                                </td>
                                <td style={{ padding:'9px 14px', fontSize:12,
                                  color:C.green, fontWeight:600 }}>
                                  {r.dateOfJoining
                                    ? dayjs(r.dateOfJoining).format('DD MMM YYYY') : '—'}
                                </td>
                                <td style={{ padding:'9px 14px' }}>
                                  {r.division
                                    ? <span style={{ fontSize:12 }}>{r.division}</span>
                                    : <span style={{ fontSize:11, fontWeight:700,
                                        color:C.red, background:'#fef2f2',
                                        padding:'1px 7px', borderRadius:8,
                                        border:'1px solid #fca5a5' }}>Missing</span>}
                                </td>
                                <td style={{ padding:'9px 14px' }}>
                                  {r.hq
                                    ? <span style={{ fontSize:12 }}>{r.hq}</span>
                                    : <span style={{ fontSize:11, fontWeight:700,
                                        color:C.red, background:'#fef2f2',
                                        padding:'1px 7px', borderRadius:8,
                                        border:'1px solid #fca5a5' }}>Missing</span>}
                                </td>
                                <td style={{ padding:'9px 14px', fontSize:12,
                                  color:C.text2 }}>{r.campusMonth || '—'}</td>
                                <td style={{ padding:'9px 14px', fontSize:11,
                                  color:C.text3, maxWidth:160 }}>{r.remarks || '—'}</td>
                                <td style={{ padding:'9px 14px', textAlign:'center' }}>
                                  <button
                                    onClick={() => openTbmEdit(r)}
                                    style={{ background:C.blue, border:'none',
                                      borderRadius:6, color:'#fff', cursor:'pointer',
                                      fontWeight:700, fontSize:11,
                                      padding:'4px 12px', display:'flex',
                                      alignItems:'center', gap:4 }}>
                                    ✏️ Map
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ padding:'8px 16px', background:C.surf,
                          borderTop:`1px solid ${C.border}`, fontSize:11, color:C.text3 }}>
                          {tbmList.length} record{tbmList.length!==1?'s':''} need mapping ·
                          <span style={{ color:C.orange, fontWeight:700 }}> Red = missing field</span>
                        </div>
                      </div>
                    )
                }
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Conversion Section — 3 tabs ── */}
      <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`,
        boxShadow:'0 1px 4px rgba(0,0,0,0.04)', overflow:'hidden', marginBottom:20 }}>

        {/* Tab header */}
        <div style={{ background:`linear-gradient(135deg,${C.navy},#2a4a9f)`,
          display:'flex', alignItems:'center', gap:0 }}>
          {[
            { key:'month',    label:'📅 Campus Month-wise' },
            { key:'campus',   label:'🏫 Campus-wise' },
            { key:'division', label:'🏢 Division-wise' },
            { key:'location', label:'📍 Location-wise' },
          ].map(t => (
            <button key={t.key} onClick={() => setConvTab(t.key)}
              style={{ padding:'12px 22px', border:'none', cursor:'pointer',
                fontWeight:700, fontSize:13,
                background: convTab===t.key ? 'rgba(255,255,255,0.18)' : 'transparent',
                color: convTab===t.key ? '#fff' : 'rgba(255,255,255,0.6)',
                borderBottom: convTab===t.key ? '3px solid #fff' : '3px solid transparent',
                transition:'all .15s' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab: Campus Month-wise ── */}
        {convTab === 'month' && (() => {
          const rows = stats.byCampusMonth || [];
          if (!rows.length) return <div style={{ padding:24, textAlign:'center', color:C.text3 }}>No data</div>;
          const tot = {
            campusCount: rows.reduce((s,r) => s+(r.campusCount||0),0),
            total:   rows.reduce((s,r) => s+r.total,0),
            active:  rows.reduce((s,r) => s+(r.active||0),0),
            inactive:rows.reduce((s,r) => s+(r.inactive||0),0),
            toBeM:   rows.reduce((s,r) => s+(r.toBeM||0),0),
            offered: rows.reduce((s,r) => s+(r.offered||0),0),
            denied:  rows.reduce((s,r) => s+(r.denied||0),0),
          };
          const activePctTot   = tot.total>0 ? Math.round((tot.active   /tot.total)*100) : 0;
          const inactivePctTot = tot.total>0 ? Math.round((tot.inactive /tot.total)*100) : 0;
          const TH = (lbl,col='rgba(255,255,255,0.75)') => (
            <th key={lbl} style={{ padding:'9px 14px', fontSize:11, fontWeight:700,
              color:col, textAlign:'center', whiteSpace:'nowrap',
              borderRight:'1px solid rgba(255,255,255,0.15)',
              textTransform:'uppercase', letterSpacing:'0.5px' }}>{lbl}</th>
          );
          const TD = (val,col=C.text1,bg='transparent',onClick=null) => (
            <td style={{ padding:'9px 14px', textAlign:'center', fontSize:13, fontWeight:700,
              color:col, background:bg, borderRight:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
              {val>0
                ? onClick
                  ? <span onClick={onClick} style={{ cursor:'pointer',
                      textDecoration:'underline dotted', textUnderlineOffset:2 }}>{val}</span>
                  : val
                : <span style={{ color:'#d1d5db' }}>0</span>}
            </td>
          );
          return (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
                <thead>
                  <tr style={{ background:C.navy }}>
                    <th style={{ padding:'9px 14px', fontSize:11, fontWeight:700,
                      color:'rgba(255,255,255,0.75)', textAlign:'left', minWidth:120,
                      position:'sticky', left:0, background:C.navy,
                      borderRight:'1px solid rgba(255,255,255,0.25)',
                      textTransform:'uppercase', letterSpacing:'0.5px' }}>Campus Month</th>
                    {TH('Campus Count','#93c5fd')}
                    {TH('Total Selected','#93c5fd')}
                    {TH('Active','#86efac')}
                    {TH('Active %','#86efac')}
                    {TH('InActive','#fca5a5')}
                    {TH('InActive %','#fca5a5')}
                    {TH('To Be Mapped','#fde68a')}
                    {TH('Offered','#fed7aa')}
                    <th style={{ padding:'9px 14px', fontSize:11, fontWeight:700,
                      color:'#fca5a5', textAlign:'center', whiteSpace:'nowrap',
                      textTransform:'uppercase', letterSpacing:'0.5px', borderRight:'none' }}>Denied</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m,i) => {
                    const bg = i%2===0?C.white:'#fafcff';
                    const expanded = expandedMonths.has(m.month);
                    const colleges = m.collegeBreakdown||[];
                    return (
                      <React.Fragment key={m.month}>
                        <tr style={{ borderBottom:expanded?'none':`1px solid ${C.border}`, background:bg }}>
                          <td style={{ padding:'9px 14px', fontSize:13, fontWeight:700,
                            color:C.navy, background:bg, position:'sticky', left:0,
                            borderRight:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              {colleges.length>0 && (
                                <button onClick={()=>toggleMonth(m.month)}
                                  style={{ width:22, height:22, borderRadius:6,
                                    background:expanded?C.navy:'#eff6ff',
                                    color:expanded?'#fff':C.navy,
                                    border:`1px solid ${C.navy}33`, cursor:'pointer',
                                    fontWeight:800, fontSize:13,
                                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                  {expanded?'▲':'▼'}
                                </button>
                              )}
                              {m.month}
                            </div>
                          </td>
                          {TD(m.campusCount,C.blue,   m.campusCount>0?'#eff6ff':'transparent',
                            m.campusCount>0 ? ()=>openDrill(`${m.month} — All Selected`, { campusMonth:m.month }) : null)}
                          {TD(m.total,      C.text1,  'transparent',
                            m.total>0 ? ()=>openDrill(`${m.month} — All Selected`, { campusMonth:m.month }) : null)}
                          {TD(m.active,     C.green,  m.active>0?'#f0fdf4':'transparent',
                            m.active>0 ? ()=>openDrill(`${m.month} — Active`, { campusMonth:m.month, status:'Active' }) : null)}
                          <td style={{ padding:'9px 14px', textAlign:'center', fontSize:12,
                            fontWeight:800, color:m.active>0?C.green:'#d1d5db',
                            borderRight:`1px solid ${C.border}` }}>
                            {m.total>0?`${Math.round((m.active/m.total)*100)}%`:'—'}
                          </td>
                          {TD(m.inactive,   C.purple, m.inactive>0?C.lpurple:'transparent',
                            m.inactive>0 ? ()=>openDrill(`${m.month} — InActive`, { campusMonth:m.month, status:'InActive' }) : null)}
                          <td style={{ padding:'9px 14px', textAlign:'center', fontSize:12,
                            fontWeight:800, color:m.inactive>0?C.purple:'#d1d5db',
                            borderRight:`1px solid ${C.border}` }}>
                            {m.total>0?`${Math.round((m.inactive/m.total)*100)}%`:'—'}
                          </td>
                          {TD(m.toBeM,      '#b45309',m.toBeM>0?'#fffbeb':'transparent',
                            m.toBeM>0 ? ()=>openDrill(`${m.month} — To Be Mapped`, { campusMonth:m.month, status:'To Be Mapped' }) : null)}
                          {TD(m.offered,    C.amber,  m.offered>0?C.lamber:'transparent',
                            m.offered>0 ? ()=>openDrill(`${m.month} — Offered`, { campusMonth:m.month, status:'Offered' }) : null)}
                          <td style={{ padding:'9px 14px', textAlign:'center', fontSize:13,
                            fontWeight:700, color:m.denied>0?C.red:'#d1d5db',
                            background:m.denied>0?C.lred:'transparent', borderRight:'none' }}>
                            {m.denied>0
                              ? <span onClick={()=>openDrill(`${m.month} — Denied`, { campusMonth:m.month, status:'Denied' })}
                                  style={{ cursor:'pointer', textDecoration:'underline dotted', textUnderlineOffset:2 }}>{m.denied}</span>
                              : '0'}
                          </td>
                        </tr>
                        {expanded && colleges.length>0 && (
                          <tr style={{ background:'#f8faff', borderBottom:`1px solid ${C.border}` }}>
                            <td colSpan={10} style={{ padding:'0 0 0 40px', borderRight:'none' }}>
                              <table style={{ width:'100%', borderCollapse:'collapse', borderLeft:`3px solid ${C.blue}` }}>
                                <thead>
                                  <tr style={{ background:'#eff6ff' }}>
                                    {['Campus Name','Total','Active','InActive','To Be Mapped','Offered','Denied'].map(h=>(
                                      <th key={h} style={{ padding:'7px 12px', fontSize:10, fontWeight:700,
                                        color:C.text2, textAlign:h==='Campus Name'?'left':'center',
                                        borderBottom:`1px solid ${C.border}`,
                                        borderRight:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {colleges.map((c,ci)=>(
                                    <tr key={c.name} style={{ borderBottom:`1px solid ${C.border}`,
                                      background:ci%2===0?C.white:'#f5f8ff' }}>
                                      <td style={{ padding:'7px 12px', fontSize:12, fontWeight:600,
                                        color:C.text1, borderRight:`1px solid ${C.border}`,
                                        maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                                        title={c.name}>🏫 {c.name}</td>
                                      {[{v:c.total,col:C.text1},{v:c.active,col:C.green},{v:c.inactive,col:C.purple},
                                        {v:c.toBeM,col:'#b45309'},{v:c.offered,col:C.amber},{v:c.denied,col:C.red,last:true}
                                      ].map((x,xi)=>(
                                        <td key={xi} style={{ padding:'7px 12px', textAlign:'center',
                                          fontSize:12, fontWeight:700, color:x.v>0?x.col:'#d1d5db',
                                          borderRight:x.last?'none':`1px solid ${C.border}` }}>
                                          {x.v>0?x.v:'—'}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background:C.navy, borderTop:`2px solid ${C.navy}` }}>
                    <td style={{ padding:'10px 14px', fontWeight:800, fontSize:13, color:'#fff',
                      position:'sticky', left:0, background:C.navy,
                      borderRight:'1px solid rgba(255,255,255,0.15)', textAlign:'left' }}>Total</td>
                    {[
                      {v:tot.campusCount,c:'#93c5fd'},
                      {v:tot.total,c:'#93c5fd'},
                      {v:tot.active,c:'#86efac'},
                      {v:`${activePctTot}%`,c:'#86efac'},
                      {v:tot.inactive,c:'#fca5a5'},
                      {v:`${inactivePctTot}%`,c:'#fca5a5'},
                      {v:tot.toBeM,c:'#fde68a'},
                      {v:tot.offered,c:'#fed7aa'},
                      {v:tot.denied,c:'#fca5a5',last:true},
                    ].map((x,xi)=>(
                      <td key={xi} style={{ padding:'10px 14px', textAlign:'center',
                        fontWeight:800, fontSize:14, color:x.c,
                        borderRight:x.last?'none':'1px solid rgba(255,255,255,0.15)' }}>{x.v}</td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })()}

        {/* ── Tab: Campus-wise (by collegeName) ── */}
        {convTab === 'campus' && <ConvBreakdownTable rows={stats.byCollegeDetailed||[]} nameLabel="Campus Name" />}

        {/* ── Tab: Division-wise ── */}
        {convTab === 'division' && <ConvBreakdownTable rows={stats.byDivisionDetailed||[]} nameLabel="Division" />}

        {/* ── Tab: Location-wise (HQ) — no To Be Mapped / Denied columns ── */}
        {convTab === 'location' && (
          <ConvBreakdownTable
            rows={stats.byLocationDetailed||[]}
            nameLabel="Location (HQ)"
            excludeCols={['toBeM','denied']}
          />
        )}

      </div>

      {/* Position + Location breakdown — hidden for now */}

      {/* ── Quick Map Modal ── */}
      <Modal
        open={!!tbmEditRec}
        onCancel={() => setTbmEditRec(null)}
        title={
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:18 }}>✏️</span>
            <div>
              <div style={{ fontWeight:800, fontSize:15, color:C.navy }}>
                Map Employee — {tbmEditRec?.name}
              </div>
              <div style={{ fontSize:11, color:C.text3, fontWeight:400 }}>
                Fill in the missing fields to complete mapping
              </div>
            </div>
          </div>
        }
        footer={null}
        width={500}
      >
        {tbmEditRec && (
          <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>

            {/* Emp Code */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:700,
                color:C.text2, marginBottom:5, textTransform:'uppercase',
                letterSpacing:'0.4px' }}>
                Emp Code {!tbmEditRec.empCode && <span style={{ color:C.red }}>* Missing</span>}
              </label>
              <Input
                value={tbmForm.empCode}
                onChange={e => setTbmForm(p => ({ ...p, empCode: e.target.value }))}
                placeholder="e.g. L04480"
                style={{ borderRadius:8,
                  borderColor: !tbmEditRec.empCode ? C.red : undefined }}
              />
            </div>

            {/* DOJ */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:700,
                color:C.text2, marginBottom:5, textTransform:'uppercase',
                letterSpacing:'0.4px' }}>
                Date of Joining
              </label>
              <DatePicker
                value={safeDay(tbmForm.dateOfJoining)}
                onChange={d => setTbmForm(p => ({
                  ...p, dateOfJoining: d ? d.format('YYYY-MM-DD') : ''
                }))}
                style={{ width:'100%', borderRadius:8 }}
                format="DD MMM YYYY"
              />
            </div>

            {/* Division + HQ side by side */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:700,
                  color:C.text2, marginBottom:5, textTransform:'uppercase',
                  letterSpacing:'0.4px' }}>
                  Division {!tbmEditRec.division && <span style={{ color:C.red }}>* Missing</span>}
                </label>
                <Input
                  value={tbmForm.division}
                  onChange={e => setTbmForm(p => ({ ...p, division: e.target.value }))}
                  placeholder="e.g. Nikkos"
                  style={{ borderRadius:8,
                    borderColor: !tbmEditRec.division ? C.red : undefined }}
                />
              </div>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:700,
                  color:C.text2, marginBottom:5, textTransform:'uppercase',
                  letterSpacing:'0.4px' }}>
                  HQ {!tbmEditRec.hq && <span style={{ color:C.red }}>* Missing</span>}
                </label>
                <Input
                  value={tbmForm.hq}
                  onChange={e => setTbmForm(p => ({ ...p, hq: e.target.value }))}
                  placeholder="e.g. Mumbai"
                  style={{ borderRadius:8,
                    borderColor: !tbmEditRec.hq ? C.red : undefined }}
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:700,
                color:C.text2, marginBottom:5, textTransform:'uppercase',
                letterSpacing:'0.4px' }}>
                Status
              </label>
              <Select
                value={tbmForm.finalStatus}
                onChange={v => setTbmForm(p => ({ ...p, finalStatus: v }))}
                style={{ width:'100%' }}>
                {STATUSES.map(s => (
                  <Select.Option key={s} value={s}>{s}</Select.Option>
                ))}
              </Select>
            </div>

            {/* Remarks */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:700,
                color:C.text2, marginBottom:5, textTransform:'uppercase',
                letterSpacing:'0.4px' }}>
                Remarks
              </label>
              <Input.TextArea
                rows={2}
                value={tbmForm.remarks}
                onChange={e => setTbmForm(p => ({ ...p, remarks: e.target.value }))}
                placeholder="Any notes…"
                style={{ borderRadius:8 }}
              />
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 }}>
              <Button onClick={() => setTbmEditRec(null)}>Cancel</Button>
              <Button
                type="primary"
                loading={tbmSaving}
                onClick={saveTbmEdit}
                style={{ background:C.navy, borderRadius:8, fontWeight:700 }}>
                💾 Save Mapping
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Count Drill-down Modal ── */}
      <Modal
        open={drillModal.visible}
        onCancel={() => setDrillModal(d => ({ ...d, visible:false }))}
        title={<span style={{ fontWeight:800, fontSize:15 }}>👥 {drillModal.title}</span>}
        width={860}
        footer={null}
        bodyStyle={{ padding:0 }}
        destroyOnClose>
        {drillModal.loading
          ? <div style={{ padding:40, textAlign:'center' }}><Spin size="large" /></div>
          : drillModal.rows.length === 0
            ? <div style={{ padding:32, textAlign:'center', color:C.text3, fontSize:13 }}>No records found</div>
            : (
              <>
                <div style={{ overflowX:'auto', maxHeight:480, overflowY:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead style={{ position:'sticky', top:0, zIndex:2 }}>
                      <tr style={{ background:C.navy }}>
                        {['#','Name','Emp Code','Position','Campus Month','Join Date','Status','Division'].map(h => (
                          <th key={h} style={{ padding:'9px 12px', color:'#fff', fontWeight:700,
                            whiteSpace:'nowrap', textAlign: h==='#' ? 'center' : 'left',
                            borderRight:'1px solid rgba(255,255,255,0.15)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {drillModal.rows.map((r, i) => {
                        const cfg = STATUS_CONFIG[r.status || r.finalStatus] || { color:C.text3, bg:C.surf, label:r.status||'—' };
                        return (
                          <tr key={r.id || i} style={{ borderBottom:`1px solid ${C.border}`,
                            background: i%2===0 ? '#fff' : '#fafcff' }}>
                            <td style={{ padding:'8px 12px', textAlign:'center', color:'#9ca3af', fontWeight:600 }}>{i+1}</td>
                            <td style={{ padding:'8px 12px', fontWeight:700, color:C.text1 }}>
                              {r.name || '—'}
                              {r.mobile && <div style={{ fontSize:11, color:C.text3 }}>📱 {r.mobile}</div>}
                            </td>
                            <td style={{ padding:'8px 12px', fontFamily:'monospace', fontWeight:700, color:C.navy }}>
                              {r.empCode || <span style={{ color:'#d1d5db' }}>—</span>}
                            </td>
                            <td style={{ padding:'8px 12px', fontWeight:600, color:C.text2 }}>{r.position || '—'}</td>
                            <td style={{ padding:'8px 12px', color:C.text2 }}>{r.campusMonth || '—'}</td>
                            <td style={{ padding:'8px 12px', color:C.green, fontWeight:600 }}>
                              {r.dateOfJoining ? dayjs(r.dateOfJoining).format('DD MMM YYYY') : '—'}
                            </td>
                            <td style={{ padding:'8px 12px' }}>
                              <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10,
                                background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.color}33` }}>
                                {cfg.label}
                              </span>
                            </td>
                            <td style={{ padding:'8px 12px', color:C.text2 }}>{r.division || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding:'10px 16px', background:C.surf, borderTop:`1px solid ${C.border}`,
                  fontSize:12, color:C.text3 }}>
                  Showing <strong style={{ color:C.navy }}>{drillModal.rows.length}</strong> record{drillModal.rows.length!==1?'s':''}
                </div>
              </>
            )
        }
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Grid View — fully designed sortable data grid
// ══════════════════════════════════════════════════════════════════
function GridView({ records, total, page, setPage,
  search, setSearch, filterStatus, setFilterStatus,
  filterPos, setFilterPos, filterMonth, setFilterMonth,
  filterJoined, setFilterJoined, campusMonths, dataPositions,
  onEdit, onDelete, onView }) {

  const PAGE = 50;
  const [sortCol, setSortCol] = React.useState('name');
  const [sortDir, setSortDir] = React.useState('asc'); // 'asc' | 'desc'

  // ── Sort logic ─────────────────────────────────────────────────
  const sorted = React.useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...records].sort((a, b) => {
      let av = a[sortCol] ?? '';
      let bv = b[sortCol] ?? '';
      // Date columns
      if (['dateOfJoining','campusDate'].includes(sortCol)) {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
        return (av - bv) * dir;
      }
      return String(av).toLowerCase().localeCompare(String(bv).toLowerCase()) * dir;
    });
  }, [records, sortCol, sortDir]);

  function SortHeader({ col, label, width, align = 'left' }) {
    const active = sortCol === col;
    const icon   = !active ? '↕' : sortDir === 'asc' ? '↑' : '↓';
    return (
      <th onClick={() => {
          if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
          else { setSortCol(col); setSortDir('asc'); }
        }}
        style={{
          width, padding:'11px 12px', cursor:'pointer', userSelect:'none',
          background: active
            ? 'rgba(255,255,255,0.18)' : 'transparent',
          textAlign: align,
          whiteSpace:'nowrap',
          transition:'background .15s',
          borderRight:'1px solid rgba(255,255,255,0.08)',
        }}>
        <div style={{ display:'flex', alignItems:'center',
          gap:4, justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
          <span style={{ fontSize:10, fontWeight:700,
            color: active ? '#fff' : 'rgba(255,255,255,0.72)',
            textTransform:'uppercase', letterSpacing:'0.5px' }}>
            {label}
          </span>
          <span style={{ fontSize:11,
            color: active ? '#fff' : 'rgba(255,255,255,0.35)',
            fontWeight: active ? 800 : 400 }}>
            {icon}
          </span>
        </div>
      </th>
    );
  }

  return (
    <div>
      {/* ── Filters bar ── */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14,
        background:C.white, padding:'14px 18px', borderRadius:12,
        border:`1px solid ${C.border}`,
        boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
        <Input prefix={<SearchOutlined style={{ color:C.text3 }}/>}
          placeholder="Search name, emp code, college, mobile, location…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          allowClear style={{ width:300, borderRadius:8 }}/>

        <Select value={filterStatus}
          onChange={v => { setFilterStatus(v); setPage(1); }}
          style={{ width:165 }}>
          <Select.Option value="all">All Status</Select.Option>
          {STATUSES.map(s => (
            <Select.Option key={s} value={s}>
              <span style={{ color:STATUS_CONFIG[s]?.color }}>{STATUS_CONFIG[s]?.icon} {s}</span>
            </Select.Option>
          ))}
        </Select>

        <Select value={filterPos}
          onChange={v => { setFilterPos(v); setPage(1); }}
          style={{ width:160 }}>
          <Select.Option value="all">All Designations</Select.Option>
          {(dataPositions || []).map(p => <Select.Option key={p} value={p}>{p}</Select.Option>)}
        </Select>

        <Select value={filterJoined}
          onChange={v => { setFilterJoined(v); setPage(1); }}
          style={{ width:155 }}>
          <Select.Option value="all">All Joining Status</Select.Option>
          <Select.Option value="true">✅ Joined</Select.Option>
          <Select.Option value="false">📋 Not Yet Joined</Select.Option>
        </Select>

        {campusMonths.length > 0 && (
          <Select value={filterMonth}
            onChange={v => { setFilterMonth(v); setPage(1); }}
            style={{ width:155 }}>
            <Select.Option value="all">All Campus Months</Select.Option>
            {campusMonths.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}
          </Select>
        )}

        <div style={{ marginLeft:'auto', display:'flex',
          alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <span style={{ fontSize:12, color:C.text3 }}>
            Showing <strong style={{ color:C.text1 }}>{sorted.length}</strong> of{' '}
            <strong style={{ color:C.text1 }}>{total}</strong> records
          </span>
          {(search || filterStatus!=='all' || filterPos!=='all' ||
            filterJoined!=='all' || filterMonth!=='all') && (
            <Button size="small" onClick={() => {
              setSearch(''); setFilterStatus('all'); setFilterPos('all');
              setFilterJoined('all'); setFilterMonth('all'); setPage(1);
            }} style={{ borderRadius:8 }}>
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background:C.white, borderRadius:14,
        border:`1px solid ${C.border}`,
        boxShadow:'0 2px 8px rgba(27,45,107,0.07)',
        overflow:'hidden' }}>

        {/* Scrollable wrapper */}
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse',
            minWidth:1100 }}>

            {/* ── Table head ── */}
            <thead>
              <tr style={{
                background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
              }}>
                <th style={{ width:50, padding:'11px 12px', textAlign:'center',
                  fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.5)',
                  textTransform:'uppercase', letterSpacing:'0.5px',
                  borderRight:'1px solid rgba(255,255,255,0.08)' }}>
                  #
                </th>
                <SortHeader col="name"           label="Candidate"      width={200}/>
                <SortHeader col="position"       label="Position"       width={80}  align="center"/>
                <SortHeader col="campusMonth"    label="Campus Month"   width={120}/>
                <SortHeader col="campusLocation" label="Location"       width={110}/>
                <SortHeader col="collegeName"    label="College"        width={160}/>
                <SortHeader col="empCode"        label="Emp Code"       width={100}/>
                <SortHeader col="dateOfJoining"  label="Date of Joining" width={120}/>
                <SortHeader col="division"       label="Division"       width={130}/>
                <SortHeader col="hq"             label="HQ"             width={90}/>
                <SortHeader col="finalStatus"    label="Status"         width={140}/>
                <th style={{ width:110, padding:'11px 12px', textAlign:'center',
                  fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.5)',
                  textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  Actions
                </th>
              </tr>
            </thead>

            {/* ── Table body ── */}
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={12} style={{ textAlign:'center',
                    padding:'60px 20px', color:C.text3 }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>🎓</div>
                    <div style={{ fontWeight:600 }}>No records found</div>
                    <div style={{ fontSize:12, marginTop:4 }}>
                      Try adjusting your filters
                    </div>
                  </td>
                </tr>
              ) : sorted.map((r, i) => {
                const rowBg = i % 2 === 0 ? C.white : '#fafcff';
                return (
                  <tr key={r.id}
                    style={{ borderBottom:`1px solid ${C.border}`,
                      transition:'background .12s', cursor:'default' }}
                    onMouseEnter={e => e.currentTarget.style.background='#eff6ff'}
                    onMouseLeave={e => e.currentTarget.style.background=rowBg}>

                    {/* # */}
                    <td style={{ padding:'12px', textAlign:'center',
                      background:rowBg, fontSize:11, color:C.text3,
                      fontWeight:600, borderRight:`1px solid ${C.border}` }}>
                      {(page-1)*PAGE+i+1}
                    </td>

                    {/* Candidate */}
                    <td style={{ padding:'12px 12px', background:rowBg }}>
                      <div style={{ fontWeight:700, fontSize:13,
                        color:C.text1, marginBottom:2 }}>
                        {r.name}
                      </div>
                      {r.mobile && (
                        <div style={{ fontSize:11, color:C.text3,
                          display:'flex', alignItems:'center', gap:4 }}>
                          📱 {r.mobile}
                        </div>
                      )}
                      {r.email && (
                        <div style={{ fontSize:11, color:C.text3,
                          display:'flex', alignItems:'center', gap:4,
                          overflow:'hidden', textOverflow:'ellipsis',
                          whiteSpace:'nowrap', maxWidth:180 }}>
                          ✉️ {r.email}
                        </div>
                      )}
                    </td>

                    {/* Position */}
                    <td style={{ padding:'12px', textAlign:'center',
                      background:rowBg }}>
                      {r.position
                        ? <Tag style={{ borderRadius:20, fontSize:11,
                            fontWeight:700, border:'none',
                            background:`${C.blue}18`, color:C.blue,
                            margin:0 }}>
                            {r.position}
                          </Tag>
                        : <span style={{ color:C.text3, fontSize:12 }}>—</span>}
                    </td>

                    {/* Campus Month */}
                    <td style={{ padding:'12px', background:rowBg }}>
                      <div style={{ fontSize:12, fontWeight:600, color:C.text1 }}>
                        {r.campusMonth || '—'}
                      </div>
                      {r.joiningMonth && r.joiningMonth !== r.campusMonth && (
                        <div style={{ fontSize:10, color:C.text3 }}>
                          Join: {r.joiningMonth}
                        </div>
                      )}
                    </td>

                    {/* Location */}
                    <td style={{ padding:'12px', background:rowBg,
                      fontSize:12, color:C.text2 }}>
                      {r.campusLocation || '—'}
                    </td>

                    {/* College */}
                    <td style={{ padding:'12px', background:rowBg }}>
                      <div style={{ fontSize:12, color:C.text2,
                        maxWidth:155, overflow:'hidden',
                        textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                        title={r.collegeName}>
                        {r.collegeName || '—'}
                      </div>
                      {r.qualification && (
                        <div style={{ fontSize:10, color:C.text3,
                          maxWidth:155, overflow:'hidden',
                          textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                          title={r.qualification}>
                          {r.qualification}
                        </div>
                      )}
                    </td>

                    {/* Emp Code */}
                    <td style={{ padding:'12px', background:rowBg }}>
                      <TBM value={r.empCode}/>
                    </td>

                    {/* Date of Joining */}
                    <td style={{ padding:'12px', background:rowBg }}>
                      {r.dateOfJoining
                        ? <div>
                            <div style={{ fontSize:12, fontWeight:600,
                              color:C.green }}>
                              {dayjs(r.dateOfJoining).format('DD MMM YYYY')}
                            </div>
                            <div style={{ fontSize:10, color:C.text3 }}>
                              {dayjs().diff(dayjs(r.dateOfJoining),'day')}d ago
                            </div>
                          </div>
                        : <TBM value={null}/>}
                    </td>

                    {/* Division */}
                    <td style={{ padding:'12px', background:rowBg }}>
                      <TBM value={r.division}/>
                    </td>

                    {/* HQ */}
                    <td style={{ padding:'12px', background:rowBg,
                      fontSize:12, color:C.text2 }}>
                      <TBM value={r.hq}/>
                    </td>

                    {/* Status */}
                    <td style={{ padding:'12px', background:rowBg }}>
                      <StatusTag status={r.finalStatus}/>
                      {r.finalStatus === 'Resigned' && r.resignationDate && (
                        <div style={{ fontSize:10, color:C.text3, marginTop:3 }}>
                          {dayjs(r.resignationDate).format('DD MMM YY')}
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td style={{ padding:'12px', textAlign:'center',
                      background:rowBg }}>
                      <div style={{ display:'flex', gap:5,
                        justifyContent:'center' }}>
                        <Tooltip title="View Details">
                          <Button size="small" icon={<EyeOutlined/>}
                            onClick={() => onView(r)}
                            style={{ borderRadius:7, border:`1px solid ${C.border}` }}/>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Button size="small" icon={<EditOutlined/>}
                            onClick={() => onEdit(r)}
                            style={{ borderRadius:7, color:C.blue,
                              borderColor:C.blue }}/>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <Popconfirm title="Delete this record?"
                            description="This action cannot be undone."
                            onConfirm={() => onDelete(r.id)}
                            okText="Delete" okButtonProps={{ danger:true }}>
                            <Button size="small" danger icon={<DeleteOutlined/>}
                              style={{ borderRadius:7 }}/>
                          </Popconfirm>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* ── Footer summary row ── */}
            {sorted.length > 0 && (
              <tfoot>
                <tr style={{ background:C.surf,
                  borderTop:`2px solid ${C.border}` }}>
                  <td colSpan={2} style={{ padding:'10px 12px',
                    fontSize:12, fontWeight:700, color:C.text2 }}>
                    {sorted.length} record{sorted.length !== 1 ? 's' : ''}
                  </td>
                  <td colSpan={7}/>
                  <td colSpan={2} style={{ padding:'10px 12px',
                    textAlign:'right', fontSize:11, color:C.text3 }}>
                    {sorted.filter(r=>r.hasJoined).length} joined ·{' '}
                    {sorted.filter(r=>r.finalStatus==='Active').length} active
                  </td>
                  <td/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── Pagination ── */}
      {total > PAGE && (
        <div style={{ display:'flex', justifyContent:'center',
          alignItems:'center', gap:8, paddingTop:16 }}>
          <Button disabled={page<=1} onClick={() => setPage(1)}
            style={{ borderRadius:8 }}>«</Button>
          <Button disabled={page<=1} onClick={() => setPage(p=>p-1)}
            style={{ borderRadius:8 }}>← Prev</Button>
          <span style={{ fontSize:12, color:C.text3,
            background:C.white, padding:'6px 14px', borderRadius:8,
            border:`1px solid ${C.border}` }}>
            Page <strong style={{ color:C.navy }}>{page}</strong> of{' '}
            <strong style={{ color:C.navy }}>{Math.ceil(total/PAGE)}</strong>
          </span>
          <Button disabled={page>=Math.ceil(total/PAGE)}
            onClick={() => setPage(p=>p+1)}
            style={{ borderRadius:8 }}>Next →</Button>
          <Button disabled={page>=Math.ceil(total/PAGE)}
            onClick={() => setPage(Math.ceil(total/PAGE))}
            style={{ borderRadius:8 }}>»</Button>
        </div>
      )}
    </div>
  );
}